import { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import Select from 'react-select';
import cn from 'classnames';
import Switch from 'react-switch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronUp, faChevronDown, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import { getUrlParam, setUrlParam, removeUrlParam } from '../../Helpers/helpers';
import { getUIKeyValueAnalytics } from '../../Helpers/ui_helpers';
import { setUIKeyValue } from '../../Actions/UIActions';
import { useApiWithCancel } from '../../CustomHooks/useApiWithCancel';
import { useUserListOptions } from './useUserListOptions';
import { useViewTranstion } from '../../CustomHooks/useViewTransition';
import { downloadCsvFromUrl } from '../../Helpers/helpers';
import { blockOnRequiringSubscription, isSubscribedToFeatureOrAdmin } from '../../Helpers/subscription_helpers';
import { getBrandMonthlyStats } from '../../APIClient/brands';
import { getBrandId, isBrand } from '../../Helpers/user_helpers';

import SelectOption from '../General/SelectOption';
import RequiresBrandLoginPanel from '../General/RequiresBrandLoginPanel';
import Tooltip from '../General/Tooltip';

import './MonthlyBrandAnalyticsResults.scss';
import MonthlyBrandAnalyticsResult from './MonthlyBrandAnalyticsResult';
import Loader from '../Loader/Loader';

const DTC_FILTER_OPTIONS = [
  { value: null, label: 'Sold Anywhere' },
  { value: true, label: 'DTC Only' },
  { value: false, label: 'Non-DTC Only' }
];

const STAT_SECTIONS = [
  {
    rows: [
      { value: 'volume', type: 'currency', label: 'Sales Volume', requiresFeature: 'ANALYTICS' },
      { value: 'emv', type: 'currency', label: 'EMV', requiresFeature: 'SOCIAL_MENTIONS_FEED' },
      { value: 'orders', type: 'scalar', label: 'Orders', requiresFeature: 'ANALYTICS' },
      { value: 'clicks', type: 'scalar', label: 'Clicks', requiresFeature: 'ANALYTICS' },
      { value: 'links', type: 'scalar', label: 'New Links', requiresFeature: 'ANALYTICS' },
      { value: 'promoters', type: 'scalar', label: 'Promoters', requiresFeature: 'ANALYTICS' }
    ]
  },
  {
    name: 'Gifting',
    rows: [
      { value: 'gifting_sent', type: 'scalar', label: 'Requests Sent', requiresFeature: 'GIFTING' },
      { value: 'gifting_accepted', type: 'scalar', label: 'Requests Accepted', requiresFeature: 'GIFTING' },
      { value: 'gifting_acceptance_rate', type: 'percentage', label: 'Acceptance Rate', requiresFeature: 'GIFTING' },
      { value: 'gifting_promoted', type: 'scalar', label: 'Lookbooks Promoted', requiresFeature: 'GIFTING' },
      { value: 'gifting_promotion_rate', type: 'percentage', label: 'Promotion Rate', requiresFeature: 'GIFTING' }
    ]
  },
  {
    name: 'Opportunities',
    rows: [
      { value: 'opportunities_sent', type: 'scalar', label: 'Requests Sent', requiresFeature: 'OPPORTUNITIES' },
      { value: 'opportunities_accepted', type: 'scalar', label: 'Requests Accepted', requiresFeature: 'OPPORTUNITIES' },
      { value: 'opportunities_acceptance_rate', type: 'percentage', label: 'Acceptance Rate', requiresFeature: 'OPPORTUNITIES' },
      { value: 'opportunities_spend', type: 'currency', label: 'Opportunities Spend', requiresFeature: 'OPPORTUNITIES' },
      { value: 'opportunities_volume', type: 'currency', label: 'Opportunities Order Volume', requiresFeature: 'OPPORTUNITIES' },
      { value: 'opportunities_emv', type: 'currency', label: 'Opportunities EMV', requiresFeature: 'OPPORTUNITIES' },
      { value: 'opportunities_roi', type: 'decimal', label: 'Average ROI', requiresFeature: 'OPPORTUNITIES' }
    ]
  }
];

const MONTHS = _.range(6).map(monthDiff => {
  const month = moment()
    .startOf('month')
    .subtract(monthDiff, 'months');

  return {
    label: month.format('MMM'),
    value: month.format('YYYY-MM-DD')
  };
});
MONTHS[0].label += ' MTD';
MONTHS.reverse();

const MonthlyBrandAnalyticsResults = props => {
  const { user, ui, analytics } = props;
  if (!isBrand(user)) return <RequiresBrandLoginPanel />;
  const brandId = getBrandId(user);

  const viewTransition = useViewTranstion();

  // TODO: dynamic titles and subtitles?
  const [title] = useState('Monthly Performance');
  const [subtitle] = useState('');
  const [hiddenSections, setHiddenSections] = useState({});

  // Stats state
  const [stats, setStats] = useState(undefined);
  const [fetchStats, cancelFetchStats] = useApiWithCancel(getBrandMonthlyStats);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isDownloadingStats, setIsDownloadingStats] = useState(false);

  // Filters state
  const { listOptions: userFilterOptions, getOptionForValue: getUserFilterOptionForValue } = useUserListOptions({ user, analytics });
  const [userFilterOption, setUserFilterOption] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('promoters')) return getUserFilterOptionForValue(params.get('promoters'));
    if (params.has('BrandList_id')) return getUserFilterOptionForValue({ BrandList_id: Number.parseInt(params.get('BrandList_id')) || 0 });
    if (params.has('DiscoverList_id')) return getUserFilterOptionForValue({ DiscoverList_id: Number.parseInt(params.get('DiscoverList_id')) || 0 });
    if (params.has('Opportunity_id')) return getUserFilterOptionForValue({ Opportunity_id: Number.parseInt(params.get('Opportunity_id')) || 0 });
    if (params.has('Lookbook_id')) return getUserFilterOptionForValue({ Lookbook_id: Number.parseInt(params.get('Lookbook_id')) || 0 });
    return userFilterOptions[0].options[0];
  });
  useEffect(() => {
    // Discover lists are loaded async so we need to update the filter if neccesary after that load
    const discoverListParam = Number.parseInt(getUrlParam('DiscoverList_id'));
    if (!discoverListParam) return;
    const option = getUserFilterOptionForValue({ DiscoverList_id: discoverListParam });
    if (!option.value) return;
    setUserFilterOption(option);
    loadStats({ userFilter: option.value, dtcOnly: dtcFilterOption.value });
  }, [userFilterOptions]);
  const [dtcFilterOption, setDtcFilterOption] = useState(() => {
    const urlValue = Number.parseInt(getUrlParam('dtc_only'));
    if (isNaN(urlValue)) return DTC_FILTER_OPTIONS[0];
    return DTC_FILTER_OPTIONS.find(option => option.value === Boolean(urlValue));
  });
  const [highlightCells, setHighlightCells] = [
    getUIKeyValueAnalytics(ui, 'performanceMapEnabled', true),
    val => props.setUIKeyValue('performanceMapEnabled', val, 'analytics')
  ];
  const filters = { userFilter: userFilterOption, dtcFilter: dtcFilterOption };

  const loadStats = async ({ userFilter, dtcOnly, download }) => {
    if (download) setIsDownloadingStats(true);
    else setIsLoadingStats(true);

    let params = {};
    if (_.isObject(userFilter)) {
      if (userFilter?.BrandList_id) params.BrandList_id = userFilter.BrandList_id;
      if (userFilter?.DiscoverList_id) params.DiscoverList_id = userFilter.DiscoverList_id;
      if (userFilter?.Lookbook_id) params.Lookbook_id = userFilter.Lookbook_id;
      if (userFilter?.Opportunity_id) params.Opportunity_id = userFilter.Opportunity_id;
    } else if (userFilter) params.filter = userFilter;

    if (!_.isNil(dtcOnly)) params.dtcOnly = dtcOnly;
    if (download) params.download = 1;

    try {
      const res = await fetchStats(brandId, params);
      if (!res) return console.warn('empty stats api response'); // request was cancelled, ignore since there should be another request in progress
      if (download) {
        if (!res.download_url) throw new Error('missing download_url');
        downloadCsvFromUrl(res.download_url);
      } else {
        if (!res.success) throw new Error('success is false', res);
        setStats(res);
      }
    } catch (e) {
      console.error('failed to load monthly stats', e);
      window.ALERT.error(`Failed to ${download ? 'download' : 'load'} monthly stats`);
    }

    setIsLoadingStats(false);
    setIsDownloadingStats(false);
  };

  const changeUserFilter = newVal => {
    const { value } = newVal;
    if (value?.BrandList_id && blockOnRequiringSubscription(user, 'LISTS')) return null;
    else if (value?.DiscoverList_id && blockOnRequiringSubscription(user, 'DISCOVER_2.0')) return null;
    else if (value?.Lookbook_id && blockOnRequiringSubscription(user, 'LOOKBOOKS')) return null;
    else if (value?.Opportunity_id && blockOnRequiringSubscription(user, 'OPPORTUNITIES')) return null;
    loadStats({ userFilter: value, dtcOnly: dtcFilterOption.value });
    viewTransition(() => setUserFilterOption(newVal));
    removeUrlParam(['promoters', 'BrandList_id', 'DiscoverList_id', 'Opportunity_id', 'Lookbook_id']);
    if (typeof value === 'string') setUrlParam('promoters', value);
    else if (value?.BrandList_id) setUrlParam('BrandList_id', value.BrandList_id);
    else if (value?.DiscoverList_id) setUrlParam('DiscoverList_id', value.DiscoverList_id);
    else if (value?.Opportunity_id) setUrlParam('Opportunity_id', value.Opportunity_id);
    else if (value?.Lookbook_id) setUrlParam('Lookbook_id', value.Lookbook_id);
  };

  const changeDtcFilter = newVal => {
    const { value } = newVal;
    loadStats({ userFilter: userFilterOption.value, dtcOnly: value });
    viewTransition(() => setDtcFilterOption(newVal));
    if (value === null) removeUrlParam('dtc_only');
    else setUrlParam('dtc_only', value ? 1 : 0);
  };

  const toggleSection = section => {
    viewTransition(() => setHiddenSections(prev => ({ ...prev, [section]: !prev[section] })));
  };

  const isSectionInvalid = section => {
    return ['Gifting', 'Opportunities'].includes(section) && !_.isNil(dtcFilterOption.value);
  };

  const isSectionHidden = section => {
    return hiddenSections[section] || isSectionInvalid(section);
  };

  const isRowEmpty = row => {
    return MONTHS.every(month => !stats[row.value][month.value]);
  };

  useEffect(() => {
    loadStats({ userFilter: userFilterOption.value, dtcOnly: dtcFilterOption.value });
    return cancelFetchStats;
  }, []);

  return (
    <div className='monthly-brand-stats'>
      {/* Header and filters */}
      <h2 className='monthly-brand-stats-subtitle'>{subtitle}</h2>
      <h1 className='monthly-brand-stats-title'>{title}</h1>
      <Select
        className='monthly-brand-stats-promoter-filter list-select'
        classNamePrefix='list-select'
        options={userFilterOptions}
        value={userFilterOption}
        onChange={changeUserFilter}
        components={{ Option: SelectOption }}
        styles={{ control: baseStyles => ({ ...baseStyles, height: '42px' }) }}
      />
      <Select
        className='monthly-brand-stats-retailers-filter list-select'
        classNamePrefix='list-select'
        options={DTC_FILTER_OPTIONS}
        value={dtcFilterOption}
        onChange={
          isSubscribedToFeatureOrAdmin(user, 'ADVANCED_TALENT_ANALYTICS')
            ? changeDtcFilter
            : () => blockOnRequiringSubscription(user, 'ADVANCED_TALENT_ANALYTICS')
        }
        components={{ Option: SelectOption }}
        styles={{ control: baseStyles => ({ ...baseStyles, height: '42px' }) }}
      />
      <button
        className='monthly-brand-stats-download'
        onClick={() => loadStats({ userFilter: userFilterOption.value, dtcOnly: dtcFilterOption.value, download: true })}
        disabled={isLoadingStats || isDownloadingStats}
      >
        {isDownloadingStats ? <Loader size={40} /> : 'Download'}
      </button>
      {/* Selected filters */}
      <ul className='monthly-brand-stats-filters'>
        {userFilterOption?.value && (
          <li className='monthly-brand-stats-filters-users'>
            <button onClick={() => changeUserFilter(userFilterOptions[0].options[0])}>
              <span>{userFilterOption.label}</span> <FontAwesomeIcon icon={faTimes} />
            </button>
          </li>
        )}
        {!_.isNil(dtcFilterOption.value) && (
          <li className='monthly-brand-stats-filters-dtc'>
            <button onClick={() => changeDtcFilter(DTC_FILTER_OPTIONS[0])}>
              <span>{dtcFilterOption.label}</span>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </li>
        )}
      </ul>
      {/* Highlight toggle */}
      <label className='monthly-brand-stats-highlight'>
        Performance Map
        <Switch
          checked={highlightCells}
          height={14}
          width={24}
          onColor='#11835A'
          offColor='#ccc'
          className='monthly-brand-stats-highlight-switch'
          checkedIcon={false}
          uncheckedIcon={false}
          onChange={setHighlightCells}
        />
      </label>
      {/* Data table */}
      <div className={cn('monthly-brand-stats-table', highlightCells && 'monthly-brand-stats-table--highlighted')}>
        <div className='monthly-brand-stats-table-header'>
          <p>KPIs</p>
          {MONTHS.map(({ label }) => (
            <p key={'header' + label}>{label}</p>
          ))}
          <p>Actions</p>
        </div>
        {STAT_SECTIONS.map(({ name, rows }) => (
          <Fragment key={name ?? 'default'}>
            {/* Section header */}
            {name && (
              <div
                key={name}
                className={cn('monthly-brand-stats-table-section', isSectionHidden(name) && 'monthly-brand-stats-table-section--hidden')}
                style={{ viewTransitionName: `table-section-${name}` }}
                data-amp-track-section={name}
              >
                <div>
                  {name}
                  {isSectionInvalid(name) && (
                    <Tooltip message={`${name} section is only filterable by promoter location`}>
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </Tooltip>
                  )}
                </div>
                {MONTHS.map(({ label }) => (
                  <p key={'subheader' + label} className='monthly-brand-stats-table-section-subheader'>
                    {label}
                  </p>
                ))}
                <button onClick={() => toggleSection(name)} disabled={isSectionInvalid(name)}>
                  <FontAwesomeIcon icon={faChevronUp} />
                </button>
              </div>
            )}

            {/* Data rows */}
            {!isSectionHidden(name) &&
              stats &&
              rows.map(row =>
                isSubscribedToFeatureOrAdmin(user, row.requiresFeature) || isRowEmpty(row) ? (
                  <MonthlyBrandAnalyticsResult key={row.value} months={MONTHS} statRow={{ stat: row, data: stats[row.value] }} filters={filters} />
                ) : (
                  <div key={row.value} className='monthly-brand-stats-table-row monthly-brand-stats-table-row--blocked'>
                    <button onClick={() => blockOnRequiringSubscription(user, row.requiresFeature)}>{row.label}</button>
                    {MONTHS.map(({ value: monthKey }, i) => (
                      <button
                        key={'blocked' + row.value + monthKey}
                        className={`monthly-brand-stats-table-row-cell monthly-brand-stats-table-row-cell--rank-${MONTHS.length - i - 2}`}
                        onClick={() => blockOnRequiringSubscription(user, row.requiresFeature)}
                      >
                        100
                      </button>
                    ))}
                    <button onClick={() => blockOnRequiringSubscription(user, row.requiresFeature)}>
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>
                  </div>
                )
              )}

            {/* Initial load placeholder rows */}
            {!isSectionHidden(name) &&
              !stats &&
              rows.map(row => (
                <div key={row.value} className='monthly-brand-stats-table-row' style={{ viewTransitionName: `table-loader-${row.value}` }}>
                  <button disabled>{row.label}</button>
                  {MONTHS.map(({ value: monthKey }) => (
                    <div key={'loader' + row.value + monthKey} className='monthly-brand-stats-table-loader'>
                      <div />
                    </div>
                  ))}
                  <button disabled>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                </div>
              ))}
          </Fragment>
        ))}

        {/* Loading overlay */}
        <div
          className={cn('monthly-brand-stats-table-overlay', stats && isLoadingStats && 'monthly-brand-stats-table-overlay--loading')}
          style={{ viewTransitionName: 'table-loader' }}
        />
      </div>
    </div>
  );
};

MonthlyBrandAnalyticsResults.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  setUIKeyValue: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, ui, analytics } = state;
  return { user, ui, analytics };
};

export default connect(mapStateToProps, { setUIKeyValue })(MonthlyBrandAnalyticsResults);
